// Запросы раздела «Настройки».

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import type {
  UniformItem,
  UniformItemIn,
  UniformItemPatch,
  UniformPrice,
  UniformPriceIn,
} from '@/shared/api/endpoints/losses';
import {
  cancelUniformPrice,
  changeUniformPrice,
  createUniformItem,
  fetchUniformItems,
  fetchUniformPrices,
  updateUniformItem,
} from '@/shared/api/endpoints/losses';
import type {
  EmploymentTypeIn,
  EmploymentTypeOut,
  EmploymentTypePatch,
  SiteTypeIn,
  SiteTypeOut,
  SiteTypePatch,
} from '@/shared/api/endpoints/org';
import {
  createEmploymentType,
  createSiteType,
  updateEmploymentType,
  updateSiteType,
} from '@/shared/api/endpoints/org';
import { orgKeys } from '@/shared/org/queries';
import { подтверждение } from '@/shared/ui/toast/подтверждение';
import type { SettingOut } from '@/shared/api/endpoints/settings';
import { fetchSettings, setSetting } from '@/shared/api/endpoints/settings';
import type { ListResponse } from '@/shared/api/types';
import type { ApiError } from '@/shared/errors/api-error';

export function useSettings(): UseQueryResult<ListResponse<SettingOut>, ApiError> {
  return useQuery<ListResponse<SettingOut>, ApiError>({
    queryKey: ['settings', 'list'],
    queryFn: () => fetchSettings(),
  });
}

/**
 * Правка настройки.
 *
 * Сбрасывает ВЕСЬ кэш раздела «Расчёт»: настройки меняют поведение расчёта — порядок
 * согласования, число недельных колонок, смещение периода, — и показанные до правки числа
 * считаны по прежнему правилу.
 */
export function useSaveSetting() {
  const client = useQueryClient();
  return useMutation<SettingOut, ApiError, { key: string; value: string }>({
    mutationFn: ({ key, value }) => setSetting(key, value),
    // Настройка меняет ПОВЕДЕНИЕ расчёта, а на экране остаётся то же поле с тем же значением.
    // Молчание здесь читается как «не сохранилось»: человек правит второй раз, и никто из них
    // двоих — ни он, ни экран — не знает, какое правило сейчас действует.
    meta: подтверждение('Настройка сохранена', 'расчёт пойдёт по новому правилу'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['settings'] });
      void client.invalidateQueries({ queryKey: ['payroll'] });
    },
  });
}

// ── Справочник спецодежды (тз, раздел 5.5: «заводит администратор в настройках») ────────────

/**
 * Справочник настроек — ВМЕСТЕ с убранными позициями.
 *
 * Иначе снятие позиции было бы дверью в одну сторону: она исчезала бы из списка, а обратного
 * пути не было бы нигде, кроме прямого запроса к базе. Ключ кэша поэтому свой: форма выдачи в
 * карточке человека спрашивает ДРУГОЙ список — только то, что выдают сегодня.
 */
export function useUniformCatalog(): UseQueryResult<ListResponse<UniformItem>, ApiError> {
  return useQuery<ListResponse<UniformItem>, ApiError>({
    queryKey: ['settings', 'uniform-items', 'с-убранными'],
    queryFn: () => fetchUniformItems(true),
  });
}

/** История цен позиции. Читается только когда её раскрыли: список её не ждёт. */
export function useUniformPrices(
  itemId: string | null,
): UseQueryResult<ListResponse<UniformPrice>, ApiError> {
  return useQuery<ListResponse<UniformPrice>, ApiError>({
    queryKey: ['settings', 'uniform-prices', itemId ?? 'нет'],
    queryFn: () => fetchUniformPrices(itemId!),
    enabled: itemId !== null,
  });
}

/**
 * Новая цена с датой, с которой она действует.
 *
 * Сбрасывает и справочник, и историю, и долги: у позиции могла поменяться «цена на сегодня», а
 * по ней считается то, что человеку насчитают за следующую выдачу.
 */
export function useChangeUniformPrice() {
  const client = useQueryClient();
  return useMutation<UniformItem, ApiError, { id: string; body: UniformPriceIn }>({
    mutationFn: ({ id, body }) => changeUniformPrice(id, body),
    meta: подтверждение('Цена записана: она действует с указанного дня'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['settings', 'uniform-items'] });
      // Форма выдачи в карточке человека держит ТОТ ЖЕ справочник под своим ключом. Пока его
      // здесь не сбрасывали, заведённая позиция появлялась в выдаче только после перезагрузки
      // страницы: экран настроек обновлялся, а соседний — нет.
      void client.invalidateQueries({ queryKey: ['uniform'] });
      void client.invalidateQueries({ queryKey: ['settings', 'uniform-prices'] });
      void client.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

/**
 * Снятие ошибочной строки цены.
 *
 * Сбрасывает то же, что и запись цены: у позиции могла поменяться «цена на сегодня» — ошибочная
 * строка как раз за сегодня и отвечала.
 */
export function useCancelUniformPrice() {
  const client = useQueryClient();
  return useMutation<UniformItem, ApiError, { id: string; priceId: string; reason: string }>({
    mutationFn: ({ id, priceId, reason }) => cancelUniformPrice(id, priceId, reason),
    meta: подтверждение('Строка убрана: в цене на дату она больше не участвует'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['settings', 'uniform-items'] });
      // Форма выдачи в карточке человека держит ТОТ ЖЕ справочник под своим ключом. Пока его
      // здесь не сбрасывали, заведённая позиция появлялась в выдаче только после перезагрузки
      // страницы: экран настроек обновлялся, а соседний — нет.
      void client.invalidateQueries({ queryKey: ['uniform'] });
      void client.invalidateQueries({ queryKey: ['settings', 'uniform-prices'] });
      void client.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

/**
 * Заведение и правка позиции.
 *
 * Сбрасывает и справочник настроек, и долги: цена позиции стоит в форме выдачи, а выдача
 * заводит долг. Показанная до правки цена — это цена, по которой человеку сейчас насчитают.
 */
export function useSaveUniformItem() {
  const client = useQueryClient();
  return useMutation<
    UniformItem,
    ApiError,
    { id?: string; body: UniformItemIn | UniformItemPatch }
  >({
    mutationFn: ({ id, body }) =>
      id === undefined
        ? createUniformItem(body as UniformItemIn)
        : updateUniformItem(id, body as UniformItemPatch),
    meta: подтверждение('Позиция спецодежды сохранена'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['settings', 'uniform-items'] });
      // Форма выдачи в карточке человека держит ТОТ ЖЕ справочник под своим ключом. Пока его
      // здесь не сбрасывали, заведённая позиция появлялась в выдаче только после перезагрузки
      // страницы: экран настроек обновлялся, а соседний — нет.
      void client.invalidateQueries({ queryKey: ['uniform'] });
      void client.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

/**
 * Убрать позицию из выдачи или вернуть её обратно.
 *
 * ## Почему «убрать», а не «удалить по-настоящему»
 *
 * Название вещи в долге человека берётся ИЗ СПРАВОЧНИКА, а не копируется в строку выдачи.
 * Стереть позицию — значит стереть слово «Берцы» из долга у всех, кому их выдавали, и
 * оставить их с суммой без предмета. Поэтому позиция снимается с выдачи: из списка выбора
 * исчезает, в истории и в долгах остаётся.
 *
 * Отдельным хуком от правки названия намеренно: у них разные слова подтверждения, а «Позиция
 * спецодежды сохранена» после нажатия «Удалить» человек читает как «не сработало».
 */
export function useSetUniformItemActive() {
  const client = useQueryClient();
  return useMutation<UniformItem, ApiError, { id: string; active: boolean; title: string }>({
    mutationFn: ({ id, active }) => updateUniformItem(id, { is_active: active }),
    meta: подтверждение<{ id: string; active: boolean; title: string }>((ввод) =>
      ввод.active
        ? `«${ввод.title}» снова выдаётся`
        : `«${ввод.title}» убрана из выдачи: в истории и в долгах осталась`,
    ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['settings', 'uniform-items'] });
      void client.invalidateQueries({ queryKey: ['uniform'] });
      void client.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

/**
 * Заведение и правка справочников оформления и типов объекта.
 *
 * Сбрасывают кэш организации целиком: тип оформления решает, в какое представление реестра
 * попадёт человек, а тип объекта — какой шаблон ведомости наследует объект, то есть какие
 * денежные колонки у него будут. Оба видны на нескольких экранах сразу.
 */
export function useSaveEmploymentType() {
  const client = useQueryClient();
  return useMutation<
    EmploymentTypeOut,
    ApiError,
    { id?: string; body: EmploymentTypeIn | EmploymentTypePatch }
  >({
    mutationFn: ({ id, body }) =>
      id === undefined
        ? createEmploymentType(body as EmploymentTypeIn)
        : updateEmploymentType(id, body as EmploymentTypePatch),
    meta: подтверждение('Тип оформления сохранён'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: orgKeys.all });
      void client.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

export function useSaveSiteType() {
  const client = useQueryClient();
  return useMutation<SiteTypeOut, ApiError, { id?: string; body: SiteTypeIn | SiteTypePatch }>({
    mutationFn: ({ id, body }) =>
      id === undefined
        ? createSiteType(body as SiteTypeIn)
        : updateSiteType(id, body as SiteTypePatch),
    meta: подтверждение('Тип объекта сохранён'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: orgKeys.all });
    },
  });
}
